import React, {useState, useRef, useEffect, useCallback} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from 'react-i18next';
import SquareButton from "../common/SquareButton";
import HoldButton from "../common/HoldButton";
import Tooltip from '@mui/material/Tooltip';
import {sendSettings} from "../../api";
import {PALETTE, rgbaToHex, hexToRgba} from "../../utils/colors";
import {getRecents, subscribeRecents, setPendingCustom} from "../../utils/recents";
import "./canvas.css"

const BRUSH_PRESETS = [
    {s: 5,  svgStroke: 1.5},
    {s: 20, svgStroke: 3.5},
    {s: 50, svgStroke: 6},
];

// Stroke-curved SVG icon
function BrushIcon({svgStroke}) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={svgStroke} strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <g transform="rotate(20 12 12)">
                <path d="M4 19h1.341a7 7 0 0 0 6.845 -5.533l.628 -2.934a7 7 0 0 1 6.846 -5.533h1.34"/>
            </g>
        </svg>
    );
}

// Brush preset buttons + popup slider.
// direction: 'vertical' (desktop sidebar) | 'horizontal' (mobile toolbar)
function BrushPresets({strokeSettings, setStrokeSettings, direction}) {
    const {t} = useTranslation();
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupPos, setPopupPos] = useState(null);
    const containerRef = useRef(null);
    const popupRef = useRef(null);

    const setBrush = useCallback((s) => {
        const next = {...strokeSettings, s};
        setStrokeSettings(next);
        sendSettings(next);
    }, [strokeSettings, setStrokeSettings]);

    // Close popup on outside click/touch or window resize
    useEffect(() => {
        if (!popupOpen) return;
        var close = function(e) {
            if (containerRef.current && containerRef.current.contains(e.target)) return;
            if (popupRef.current && popupRef.current.contains(e.target)) return;
            setPopupOpen(false);
        };
        var closeOnResize = function() { setPopupOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close);
        window.addEventListener('resize', closeOnResize);
        return function() {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('touchstart', close);
            window.removeEventListener('resize', closeOnResize);
        };
    }, [popupOpen]);

    const handlePresetClick = useCallback((s) => {
        setBrush(s);
        if (containerRef.current) {
            var rect = containerRef.current.getBoundingClientRect();
            if (direction === 'vertical') {
                setPopupPos({left: rect.right, top: rect.top});
            } else {
                // horizontal: appear above the buttons
                setPopupPos({left: rect.left, top: rect.top, transform: 'translateY(-100%)'});
            }
        }
        setPopupOpen(true);
    }, [setBrush, direction]);

    var popupStyle = popupPos
        ? {position: 'fixed', zIndex: 9999, left: popupPos.left, top: popupPos.top, transform: popupPos.transform}
        : {display: 'none'};

    const sliderEl = (
        <div ref={popupRef} className={'brush-popup brush-popup--' + direction} style={popupStyle}>
            <input
                type="range"
                min="1"
                max="150"
                value={strokeSettings.s}
                className={'BrushSizeSlider' + (direction === 'horizontal' ? ' BrushSizeSlider--h' : '')}
                data-brush-size-slider="true"
                onChange={function(e) { setBrush(Number(e.target.value)); }}
            />
        </div>
    );

    const buttons = BRUSH_PRESETS.map(function({s, svgStroke}) {
        var active = strokeSettings.s === s;
        var placement = direction === 'horizontal' ? 'top' : 'right';
        return (
            <Tooltip key={s} title={t('tools.brushSize', {size: s})} placement={placement}>
                <button
                    className={'brush-preset' + (active ? ' brush-preset--active' : '')}
                    style={direction === 'horizontal' ? {margin: '0 -1px 0 0'} : {marginTop: -1}}
                    onClick={function() { handlePresetClick(s); }}
                    data-brush-size={s}
                    data-brush-active={active ? 'true' : 'false'}
                >
                    <BrushIcon svgStroke={svgStroke}/>
                </button>
            </Tooltip>
        );
    });

    return (
        <div ref={containerRef} style={{position: 'relative', display: direction === 'horizontal' ? 'flex' : 'block'}}>
            {buttons}
            {popupOpen && ReactDOM.createPortal(sliderEl, document.body)}
        </div>
    );
}

function MobileColorPicker({color, onColorChange, popoverPosition}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const btnRef = useRef(null);
    const colorInputRef = useRef(null);
    const [popStyle, setPopStyle] = useState({});
    const [recents, setRecents] = useState(getRecents());

    useEffect(function() {
        return subscribeRecents(function() { setRecents(getRecents()); });
    }, []);

    useEffect(() => {
        if (!open) return;
        // Use touchstart+mousedown instead of pointerdown — pointer capture on
        // the canvas redirects pointerdown events away from the popover on mobile.
        var close = function(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target)
                && btnRef.current && !btnRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close);
        return function() {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('touchstart', close);
        };
    }, [open]);

    useEffect(() => {
        if (!open || !btnRef.current) return;
        var r = btnRef.current.getBoundingClientRect();
        if (popoverPosition === 'right') {
            setPopStyle({position: 'fixed', left: r.right + 4, top: r.top, zIndex: 9999});
        } else {
            setPopStyle({position: 'fixed', left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 4, transform: 'translateX(-50%)', zIndex: 9999});
        }
    }, [open, popoverPosition]);

    const hex = rgbaToHex(color);

    return (
        <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
            <button
                ref={btnRef}
                className="brush-preset"
                onClick={() => setOpen(o => !o)}
            >
                <span style={{
                    width: 22, height: 22, borderRadius: 4,
                    background: color || '#000000',
                    border: '2px solid rgba(255,255,255,0.6)',
                    display: 'block', boxSizing: 'border-box',
                }}/>
            </button>
            {open && ReactDOM.createPortal(
                <div ref={popoverRef} className="mobile-color-popover" style={popStyle}
                     onTouchStart={function(e) { e.stopPropagation(); }}
                     onMouseDown={function(e) { e.stopPropagation(); }}>
                    <div className="mobile-color-grid">
                        {PALETTE.map(c => (
                            <button
                                key={c}
                                className={'mobile-color-swatch' + (hex === c.toLowerCase() ? ' mobile-color-swatch--active' : '')}
                                style={{background: c}}
                                onClick={() => {
                                    onColorChange(hexToRgba(c));
                                    setOpen(false);
                                }}
                            />
                        ))}
                    </div>
                    <div className="mobile-color-grid">
                        {[0,1,2,3,4].map(function(idx) {
                            var c = recents[idx];
                            if (c) {
                                return (
                                    <button
                                        key={'r' + idx}
                                        className={'mobile-color-swatch' + (hex === c.toLowerCase() ? ' mobile-color-swatch--active' : '')}
                                        style={{background: c}}
                                        onClick={function() {
                                            onColorChange(hexToRgba(c));
                                            setOpen(false);
                                        }}
                                    />
                                );
                            }
                            return (
                                <button
                                    key={'r' + idx}
                                    className="mobile-color-swatch mobile-color-swatch--empty"
                                    onClick={function() { if (colorInputRef.current) colorInputRef.current.click(); }}
                                />
                            );
                        })}
                    </div>
                    <div style={{position: 'relative'}}>
                        <input
                            ref={colorInputRef}
                            type="color"
                            value={hex}
                            onChange={(e) => {
                                setPendingCustom(e.target.value);
                                onColorChange(hexToRgba(e.target.value));
                                setOpen(false);
                            }}
                            className="mobile-color-custom"
                            title={t('tools.customColor')}
                        />
                        <span style={{
                            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                            fontSize: 16, fontWeight: 'bold', pointerEvents: 'none', lineHeight: 1,
                            color: '#fff', letterSpacing: 2,
                            textShadow: '0 1px 2px #000, 0 -1px 2px #000, 1px 0 2px #000, -1px 0 2px #000',
                        }}>...</span>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function TapToClear({onClear}) {
    const {t} = useTranslation();
    const [armed, setArmed] = useState(false);
    const timerRef = useRef(null);

    const reset = useCallback(() => {
        setArmed(false);
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }, []);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const handleTap = () => {
        if (armed) {
            reset();
            if (onClear) onClear();
        } else {
            setArmed(true);
            timerRef.current = setTimeout(reset, 2000);
        }
    };

    return (
        <div onClick={handleTap} style={{cursor: 'pointer'}}>
            {armed
                ? <button style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 36, height: 36, border: '1px solid #666', borderRadius: 0,
                    padding: 0, margin: 0, cursor: 'pointer', boxSizing: 'border-box',
                    background: '#d32f2f', WebkitTapHighlightColor: 'transparent', outline: 'none',
                  }}>
                    <span style={{fontSize: 9, color: '#fff', fontWeight: 700, letterSpacing: 0.5, lineHeight: 1}}>{t('tools.tapClear')}</span>
                  </button>
                : <SquareButton icon="clear" />
            }
        </div>
    );
}

export default function Tools(props) {
    const {t} = useTranslation();
    const disabled = props.drawingDisabled;
    const disabledStyle = disabled ? {opacity: 0.4, pointerEvents: 'none'} : {};
    const horizontal = props.horizontal;
    const compact = props.compact; // vertical layout with mobile-level controls

    if (compact) {
        return (
            <div className="tools Col" style={disabledStyle}>
                <Tooltip title={t('tools.pencil')} placement="right-end">
                    <div>
                        <SquareButton icon="pen"
                                      onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 0})}
                                      selected={props.strokeSettings.t === 0 ? true : undefined}/>
                    </div>
                </Tooltip>
                <Tooltip title={t('tools.eraser')} placement="right-end">
                    <div>
                        <SquareButton icon="erase"
                                      onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 1})}
                                      selected={props.strokeSettings.t === 1 ? true : undefined}/>
                    </div>
                </Tooltip>
                <MobileColorPicker
                    color={props.strokeSettings.c}
                    onColorChange={(c) => {
                        const next = {...props.strokeSettings, c, t: 0};
                        props.setStrokeSettings(next);
                        sendSettings(next);
                    }}
                    popoverPosition="right"
                />
                <div style={{height: 6}}/>
                <BrushPresets strokeSettings={props.strokeSettings} setStrokeSettings={props.setStrokeSettings} direction="vertical"/>
                <div style={{height: 6}}/>
                <Tooltip title={t('tools.undoCtrlZ', 'Undo (Ctrl+Z)')} placement="right-end">
                    <div>
                        <SquareButton icon="undo" onClick={props.onUndo}/>
                    </div>
                </Tooltip>
                <TapToClear onClear={props.handleClear}/>
            </div>
        );
    }

    if (horizontal) {
        return (
            <div className="tools-horizontal" style={disabledStyle}>
                {/* Left group: draw tools + size presets */}
                <div className="tools-h-group">
                    <Tooltip title={t('tools.pencil')} placement="top">
                        <div>
                            <SquareButton icon="pen"
                                          onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 0})}
                                          selected={props.strokeSettings.t === 0 ? true : undefined}/>
                        </div>
                    </Tooltip>
                    <Tooltip title={t('tools.eraser')} placement="top">
                        <div>
                            <SquareButton icon="erase"
                                          onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 1})}
                                          selected={props.strokeSettings.t === 1 ? true : undefined}/>
                        </div>
                    </Tooltip>

                    <MobileColorPicker
                        color={props.strokeSettings.c}
                        onColorChange={(c) => {
                            const next = {...props.strokeSettings, c, t: 0};
                            props.setStrokeSettings(next);
                            sendSettings(next);
                        }}
                    />

                    <div className="tools-h-separator"/>

                    <BrushPresets strokeSettings={props.strokeSettings} setStrokeSettings={props.setStrokeSettings} direction="horizontal"/>
                </div>

                {/* Right group: undo + clear + publish */}
                <div className="tools-h-group" style={{marginLeft: 'auto'}}>
                    <Tooltip title={t('tools.undoCtrlZ', 'Undo (Ctrl+Z)')} placement="top">
                        <div>
                            <SquareButton icon="undo" onClick={props.onUndo}/>
                        </div>
                    </Tooltip>
                    <TapToClear onClear={props.handleClear}/>
                </div>
            </div>
        );
    }

    return (
        <div className="tools Col">
            <div style={disabledStyle}>
                <Tooltip title={t('tools.pencil')} placement="right-end">
                    <div>
                        <SquareButton icon="pen"
                                      onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 0})}
                                      selected={props.strokeSettings.t === 0 ? true : undefined}/>
                    </div>
                </Tooltip>
                <Tooltip title={t('tools.eraser')} placement="right-end">
                    <div>
                        <SquareButton icon="erase"
                                      onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 1})}
                                      selected={props.strokeSettings.t === 1 ? true : undefined}/>
                    </div>
                </Tooltip>
                {!props.isGuessMode && (
                <Tooltip title={t('tools.colorPickerCtrl', 'Color picker (Hold Ctrl)')} placement="right-end">
                    <div>
                        <SquareButton icon="pipette"
                                      onClick={() => props.setStrokeSettings({...props.strokeSettings, t: 2})}
                                      selected={props.strokeSettings.t === 2 ? true : undefined}/>
                    </div>
                </Tooltip>
                )}
                <div style={{height: "15px"}}/>

                <BrushPresets strokeSettings={props.strokeSettings} setStrokeSettings={props.setStrokeSettings} direction="vertical"/>

                <div style={{height: "15px"}}/>

                <Tooltip title={t('tools.undoCtrlZ')} placement="right-end">
                    <div>
                        <SquareButton icon="undo" onClick={props.onUndo}/>
                    </div>
                </Tooltip>
                <Tooltip title={t('tools.holdToClearEverything')} placement="right-end">
                    <div>
                        <HoldButton onClick={props.handleClear}/>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
}
